import * as React from 'react';
import { Link, Redirect } from 'react-router-dom';
import Searchbox from '../Controls/SearchBox';
import * as i18next from 'i18next';
import { withTranslation } from 'react-i18next';
import Spinner from '../Controls/Spinner';
import { useSearchSites, SiteInfo } from '../../hooks/useSiteInfo';
import { filter as filterOptions } from '../../constants/filter';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import Chip from '@mui/material/Chip';
import { isRecentlyUpdated } from '../../constants/recentlyUpdated';
import { DataGridPremium, GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';

function AdminSitesPage() {
    const [inputValue, setInputValue] = React.useState('');
    const [committedQuery, setCommittedQuery] = React.useState<string | null>(null);
    const [, setActiveFilterText] = React.useState(i18next.t('site.all'));
    const [activeFilterValue, setActiveFilterValue] = React.useState('All');

    const { data: siteInfos = [], isFetching, isSuccess } = useSearchSites(committedQuery, activeFilterValue);

    if (isSuccess && siteInfos.length === 1) {
        return <Redirect to={'/administrationsiteinfo/' + siteInfos[0].uniqueSiteId} />;
    }

    function handleSearchQueryChange(e: React.ChangeEvent<HTMLInputElement>) {
        const { value } = e.target;
        setInputValue(value);
        if (!value) setCommittedQuery('');
    }

    function handleChangeFilter(e: { target: { value: string } }) {
        setActiveFilterValue(e.target.value);
        const found = filterOptions.find(f => f.filterKey === e.target.value);
        setActiveFilterText(found ? i18next.t(found.languageKey) : e.target.value);
    }

    function searchSites(e: React.FormEvent) {
        e.preventDefault();
        setCommittedQuery(inputValue);
    }

    const columns: GridColDef<SiteInfo>[] = [
        {
            field: 'siteName',
            headerName: i18next.t('site.SiteName'),
            flex: 2,
            renderCell: (params: GridRenderCellParams<SiteInfo>) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {params.value}
                    {isRecentlyUpdated(params.row.updateDateUTC) && (
                        <Chip
                            size="small"
                            label={i18next.t('site.recentlyUpdated')}
                            data-testid="recently-updated"
                            color="warning"
                            sx={{ height: 18, fontWeight: 600, '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem' } }}
                        />
                    )}
                </Box>
            ),
        },
        {
            field: 'streetAddress',
            headerName: i18next.t('site.StreetAddress'),
            flex: 2,
        },
        {
            field: 'postalCode',
            headerName: i18next.t('site.PostalCode'),
            flex: 1,
        },
        {
            field: 'town',
            headerName: i18next.t('site.Town'),
            flex: 1,
        },
        {
            field: 'customerNumber',
            headerName: i18next.t('site.CustomerNumber'),
            flex: 1,
        },
        {
            field: 'published',
            headerName: i18next.t('site.State'),
            flex: 1,
            renderCell: (params: GridRenderCellParams<SiteInfo>) => (
                <Chip
                    label={params.value ? i18next.t('site.published') : i18next.t('site.draft')}
                    size="small"
                    color={params.value ? 'success' : 'default'}
                    variant="filled"
                />
            ),
        },
        {
            field: 'actions',
            headerName: '',
            width: 60,
            sortable: false,
            renderCell: (params: GridRenderCellParams<SiteInfo>) => (
                <IconButton
                    component={Link}
                    to={'/administrationsiteinfo/' + params.row.uniqueSiteId}
                    size="small"
                    color="primary"
                >
                    <EditIcon fontSize="small" />
                </IconButton>
            ),
        },
    ];

    return (
        <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <img src={new URL('../../img/searchtitleicon.png', import.meta.url).href} alt="" />
                    <Typography variant="h5" fontWeight={600}>
                        {i18next.t('headers.searchsite')}
                    </Typography>
                </Box>
                <Button
                    component={Link}
                    to={'administrationsiteinfo/0/true'}
                    variant="contained"
                    color="secondary"
                    startIcon={<AddIcon />}
                    size="small"
                >
                    {i18next.t('adminsites.addsite')}
                </Button>
            </Box>

            <Box sx={{ mb: 2 }}>
                <Searchbox
                    searchSite={searchSites}
                    handleChange={handleSearchQueryChange}
                    searchQuery={inputValue}
                    activeFilterValue={activeFilterValue}
                    handleChangeFilter={handleChangeFilter}
                />
            </Box>

            {isFetching ? <Spinner /> : (
                <DataGridPremium
                    rows={siteInfos}
                    columns={columns}
                    getRowId={(row) => row.uniqueSiteId}
                    disableColumnMenu
                    disableColumnResize
                    hideFooter
                    density="compact"
                    sx={{ border: '1px solid', borderColor: 'divider' }}
                    localeText={{
                        noRowsLabel: committedQuery && isSuccess
                            ? i18next.t('site.nosite')
                            : i18next.t('site.searchToDisplay'),
                    }}
                />
            )}
        </Box>
    );
}

export default withTranslation('translations')(AdminSitesPage);
